import React, { useMemo, forwardRef, useCallback } from 'react';
import { Button, Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import _ from 'lodash';
import { useRef } from 'react';
import { useState } from 'react';

const initialFormValues = {
    product: undefined,
    subProduct: undefined,
    environment: undefined,
    projectList: [{ repository: undefined }]
};

export const DELIMITER = ":";
export const VERSION_DELIMITER = ";"

const GithubCodeScanMappingFormV2 = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
    const repositeryRef = useRef({});
    const branchsRef = useRef({});
    const branchsApiRef = useRef([]);
    const [form] = Form.useForm();
    const [initialValues] = useState(() => {
        if (props.record) {
            return [];
        }
        return [{ ...initialFormValues }];
    });

    const [mappedRepoBranchList] = useState(() => {
        // this state contains the already mapped projects/branches
        // This is stored because the repository and branch dropdowns are paginated
        // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
        if (props.record) {
            const { branchName, repositoryName } = props.record;
            const repositoryNameArray = repositoryName.split(",");
            let repositories = [], branchList = {}
            if (_.isEmpty(branchName)) {
                repositories = repositoryNameArray.map(repository => ({ id: repository, name: repository }))
            } else {
                const branchNameArray = branchName.split(",");
                _.forEach(branchNameArray, (branches, i) => {
                    const [repository, branch] = branches.split(DELIMITER);
                    repositeryRef.current = { ...repositeryRef.current, [repository]: repository }
                    repositories.push({ id: repository, name: repository })
                    if (branch) {
                        branchsRef.current = { ...branchsRef.current, [branch]: branch }
                        branchList[repository] = ({ id: branch, name: branch })
                    }
                });
            }

            return {
                repositories,
                branches: branchList
            }
        }
        return { repositories: [], branches: {} }
    })

    const fetchBranch = useCallback((params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
        return {
            promise: promise.then(response => {
                // merging the already mapped branches to the fetched list so that if the selected value is not in the option list
                // then id should not be displayed
                if (mappedRepoBranchList.branches[params.repository] && !_.isEmpty(response.projects)) {
                    response.projects = [...response.projects, mappedRepoBranchList.branches[params.repository]];
                }
                _.forEach(response.projects, p => {
                    branchsRef.current[p.id] = p.name;
                });
                return response;
            }),
            cancel
        }
    }, [props.loginConfigId, props.toolName, mappedRepoBranchList]);

    const fetchRepository = useCallback((params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
        return {
            promise: promise.then(response => {
                // merging the already mapped repositories to the fetched list so that if the selected value is not in the option list
                // then id should not be displayed
                if (mappedRepoBranchList.repositories.length > 0 && !_.isEmpty(response.projects)) {
                    response.projects = [...response.projects, ...mappedRepoBranchList.repositories];
                }
                _.forEach(response.projects, p => {
                    repositeryRef.current[p.id] = p.name;
                });

                return response;
            }),
            cancel
        }
    }, [props.loginConfigId, props.toolName, mappedRepoBranchList]);

    const getExtraFieldsData = useCallback((config) => {
        const { projectList } = config;
        const repositryBranchNameList = [];
        const repositeryNameList = _.map(projectList, ({ repository, branch }) => {
            const repositoryName = repositeryRef.current[repository];
            const branchName = branchsRef.current[branch];
            if (branch) {
                repositryBranchNameList.push(repositoryName + DELIMITER + branchName);
            } else {
                repositryBranchNameList.push(repositoryName);
            }
            return repositoryName;
        });

        return ({
            repositoryName: repositeryNameList.join(),
            branchName: repositryBranchNameList.join(),
            //send the property received in config
            ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        });
    }, []);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { branchName, repositoryName } = config;
        let projects;
        if (_.isEmpty(branchName)) {
            projects = _.map(repositoryName.split(','), repo => {
                return { repository: repo }
            });
        } else {
            const repositoryWithBranch = branchName.split(',');
            projects = _.map(repositoryWithBranch, repo => {
                const index = repo.lastIndexOf(DELIMITER);
                if (index !== -1) {
                    const repository = repo.substring(0, index);
                    const branch = repo.substring(index + 1, repo.length);
                    return { repository, branch: branch };
                }
                return { repository: repo }
            });
        }

        return ({
            projectList: projects
        });
    }, []);

    const onValuesChange = useCallback((values, ...args) => {
        if (values.configurations) {
            let isRepositoryChanged = false;
            const formValues = form.getFieldsValue();
            _.forEach(values.configurations, (configs, index) => {

                // If repository list is greater than 1, means that a new blank row is added
                // no need to clear the branch fields
                if (!configs || !configs.projectList || configs.projectList.length > 1) {
                    return;
                }

                // since this is a list field, onchange will trigger only for the changed row.
                // if change is done at 2nd row, data received in this callback will only contain 2nd row data
                // configs.projectList is given as string when we search in the field
                if (!configs || _.isString(configs.projectList)) {
                    return;
                }
                _.forEach(configs.projectList, (object, i) => {

                    // since this is a list field, onchange will trigger only for the changed row.
                    // if change is done at 2nd row, data received in this callback will only contain 2nd row data
                    // Also checking if the object contains `repository` and nothing else 
                    // because we need to reset the branch field only if the repository is changed
                    // and this change is also triggered when rows are added or deleted, so we need to ignore it
                    if (object && object.repository && Object.keys(object).length === 1) {
                        isRepositoryChanged = true;
                        formValues.configurations[index].projectList[i].branch = undefined;
                    }
                })
            });
            if (isRepositoryChanged) {
                form.setFieldsValue(formValues);
            }
        }
    }, [form]);

    const projectListRender = useCallback((listField) => {

        // This is a hack, we are storing the api method for branch in a ref
        // as it is dependant on `listField` param of this method
        // that is why we cannot move it to `useCallback`
        // For a `listField.name` we are defining the method only once and using the method if it already exists in the ref
        // Reason for doing this is that the method reference will always change and will trigger API on every render
        // It also causes a visible bug on UI as when the API triggers, the selected value disappears as we don't show value when data is loading
        let branchAPI = branchsApiRef.current[listField.name];
        if (!branchAPI) {
            branchAPI = branchsApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
                const allConfigs = form.getFieldsValue().configurations;
                const repository = allConfigs[listField.name]?.projectList[nestedListFieldName]?.repository;
                if (!repository) {
                    return { promise: Promise.resolve([]), cancel: _.noop };
                }
                return fetchBranch({ ...params, project: repository });
            }
        }
        // 

        const __fields = [{
            name: 'repository',
            label:"Repository",
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            disabled: disableProjectFields,
            colSpan: 16,
            props: {
                showValueOnload: false,
                autoFocus: !disableProjectFields,
                optionsMutator: (repositories) => {
                    const selected = _.compact(_.map(form.getFieldsValue().configurations?.[listField.name]?.projectList, p => p?.repository));
                    return _.map(repositories, p => ({ ...p, disabled: _.includes(selected, p.id) }));
                },
                api: fetchRepository
            },
            rules: [{ required: true, message: 'Blackduck Hub Project is required' }]
        }, {
            name: 'branch',
            label:"Branch",
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            disabled: disableProjectFields,
            colSpan: 16,
            props: {
                placeholder: "Leave blank to fetch from default branch",
                setExtraProps: (field, nestedListField) => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const repository = allConfigs[listField.name]?.projectList[nestedListField.name]?.repository;
                    return {
                        api: branchAPI,
                        showValueOnload: false,
                        resetOptionsProp: repository
                    }
                }
            },
        }, {
            name: "add",
            label: " ",
            colSpan: 2,
            render: ({ remove, name, fields }) => {
                return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
            }
        }]

        return (
            <div className='m-b-sm'>
                <Form.List name={[listField.name, "projectList"]}>
                    {(fields, { add, remove }) => {
                        return (
                            <div>
                                {_.map(fields, field => {
                                    return generateFormFields(__fields, 9, { ...field/* , name: [listField.name, field.name] */, add, remove, fields }, field.name);
                                })}
                                <Button size='small' type='dashed' onClick={() => add({ repository: undefined })} disabled={disableProjectFields}>+ Add Repository</Button>
                            </div>
                        )
                    }}
                </Form.List>
            </div>
        )
    }, [disableProjectFields, form, fetchRepository, fetchBranch]);

    const _fields = useMemo(() => [
    {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender
    },
    {
        type: FieldTypes.DIVIDER
    }], [projectListRender]);

    return (
        <GenericMappingForm
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            extraFields={_fields}
            formDefaultValues={initialValues}
            newRowDefaultValue={initialValues[0]}
            {...props}
            onValuesChange={onValuesChange}
        />
    )
})

export default GithubCodeScanMappingFormV2;
